// Integration session/consult-note link api fns — carved verbatim from
// `src/api/scribeAPIV2.ts` (which now re-exports them) as D1 of the
// `integrations/ehr` family (ARCFE-1087).
//
// `getIntegrationLinkedSessionInfo` builds its params imperatively and omits
// absent keys rather than sending `undefined`; that shape is carried across
// unchanged because the backend treats a present-but-empty filter differently
// from an absent one.
import type {
  CreateIntegrationSessionWorkflowRequest,
  CreateIntegrationSessionWorkflowResponse,
  GetIntegrationLinkedConsultNoteInfoResponse,
  GetIntegrationLinkedSessionInfoResponse,
  HydrateIntegrationPatientDemographicsResponse,
  PatchSessionLinkRequest,
} from '../../contracts/types';
import { http } from './ehrHttp';

/**
 * The hydrate endpoint runs a Veradigm GetToken + GetUserAuthentication +
 * GetPatient chain (p99 near 3 s; a queue-bound clinic can take tens of
 * seconds). The caller has already navigated and fires this without awaiting,
 * so bound it rather than let a stalled upstream hold the promise open.
 *
 * MUST stay above the backend's own deadline (`_HYDRATE_ENDPOINT_TIMEOUT_SECONDS`
 * = 20 s in ml-scribe), which answers 200 `hydrated: false` when it runs out.
 * The backend shields the read from client disconnects, so a client that gave
 * up first would miss a write that still lands and never refresh the caches;
 * the session query's long `staleTime` means the header would stay blank.
 */
const HYDRATE_PATIENT_DEMOGRAPHICS_TIMEOUT_MS = 25_000;

export const getIntegrationLinkedSessionInfo = ({
  sessionId,
  provider_unique_identifier,
}: {
  sessionId?: string;
  provider_unique_identifier?: string;
}) => {
  const params: Record<string, string> = {};
  if (sessionId) {
    params.session_id = sessionId;
  }
  if (provider_unique_identifier) {
    params.provider_unique_identifier = provider_unique_identifier;
  }

  return http().get<GetIntegrationLinkedSessionInfoResponse>(
    '/integrations/links/sessions',
    {
      params,
    }
  );
};

export const getIntegrationLinkedConsultNoteInfo = ({
  consultNoteId,
}: {
  consultNoteId: string;
}) => {
  return http().get<GetIntegrationLinkedConsultNoteInfoResponse>(
    `/integrations/links/consult-note-link`,
    {
      params: {
        consult_note_id: consultNoteId,
      },
    }
  );
};

export const patchIntegrationSessionLink = ({
  request,
  sessionId,
}: {
  request: PatchSessionLinkRequest;
  sessionId: string;
}) => {
  return http().patch<GetIntegrationLinkedSessionInfoResponse>(
    `/integrations/links/sessions/${sessionId}`,
    request
  );
};

export const createIntegrationWorkflowSession = (
  request: CreateIntegrationSessionWorkflowRequest
) => {
  return http().post<CreateIntegrationSessionWorkflowResponse>(
    `/integrations/workflows/session`,
    request
  );
};

/**
 * Asks the backend to read the patient's demographics (DOB, sex) from the EHR
 * and write them onto the linked profile. Called after a session created from a
 * Veradigm schedule row has loaded, so the vendor round trip never sits on the
 * session-create path. 200 with `hydrated: false` on a vendor failure.
 */
export const hydrateIntegrationPatientDemographics = ({
  patientProfileId,
}: {
  patientProfileId: string;
}) => {
  return http().post<HydrateIntegrationPatientDemographicsResponse>(
    `/integrations/patient-profiles/${patientProfileId}/hydrate-demographics`,
    undefined,
    { timeout: HYDRATE_PATIENT_DEMOGRAPHICS_TIMEOUT_MS }
  );
};
